import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
    notifyUserGroupMemberRemoved,
    notifyUserGroupMembersAdded,
    notifyUserGroupPermissionSet,
    notifyUserGroupPermissionsSet,
    notifyUserGroupRemoved,
    notifyUserGroupSaved
} from '../../../common/signalr/signalrActions';
import { RootEpic } from '../../../store';
import {
    createEpicFromAsyncAction,
    createEpicFromAsyncActionEmptyRequest,
    getAllPricelistChildren
} from '../../../utils';
import { pricePrgError } from '../../ui/redux/uiSlices';
import {
    addMembersToGroup,
    createUserGroup,
    fetchUserGroups,
    removeMemberFromGroup,
    removeUserGroup,
    setPagePermissionForGroupRequest,
    setPermissionForGroupSuccess,
    setPermissionsForGroupSuccess,
    setPricelistRecordPermissionsForGroupRequest,
    setRecordPermissionForGroupRequest,
    updateUserGroup
} from './userGroupSlices';

const fetchUserGroups$ = createEpicFromAsyncActionEmptyRequest(fetchUserGroups, ({ userGroupClient }) =>
    userGroupClient.getAllUserGroups()
);

const createUserGroup$ = createEpicFromAsyncAction(
    createUserGroup,
    (payload, { userGroupClient }) =>
        userGroupClient.createUserGroup(payload).then(id => ({
            name: payload.name,
            description: payload.description ?? '',
            userGroupId: id,
            members: [],
            permissions: [],
            disableInactivateArticleEmails: payload.disableInactivateArticleEmails,
            hideWarehouseBalance: payload.hideWarehouseBalance
        })),
    (req, resp) => notifyUserGroupSaved(resp.userGroupId)
);

const updateUserGroup$ = createEpicFromAsyncAction(
    updateUserGroup,
    (payload, { userGroupClient }) =>
        userGroupClient.updateUserGroup(payload.userGroupId, payload.dto).then(() => payload),
    req => notifyUserGroupSaved(req.userGroupId)
);

const addMembersToGroup$ = createEpicFromAsyncAction(
    addMembersToGroup,
    (payload, { userGroupClient }) =>
        userGroupClient.addMembersToUserGroup(payload.userGroupId, payload.userIds).then(() => payload),
    req => notifyUserGroupMembersAdded(req)
);

const removeMemberFromGroup$ = createEpicFromAsyncAction(
    removeMemberFromGroup,
    (payload, { userGroupClient }) =>
        userGroupClient.removeMemberFromGroup(payload.userGroupId, payload.userId).then(() => payload),
    req => notifyUserGroupMemberRemoved(req)
);

const setGroupPagePermission$ = createEpicFromAsyncAction(
    {
        request: setPagePermissionForGroupRequest,
        success: setPermissionForGroupSuccess
    },
    (payload, { userGroupClient }) =>
        userGroupClient
            .setPagePermissionForGroup(payload.userGroupId, payload.resourceType, payload.hasAccess)
            .then(userGroupPermissionId => ({
                userGroupPermissionId,
                canCreate: false,
                canDelete: false,
                canUpdate: false,
                canRead: payload.hasAccess,
                resource: payload.resourceType,
                userGroupId: payload.userGroupId
            })),
    (req, resp) => notifyUserGroupPermissionSet(resp)
);

const setGroupRecordPermission$ = createEpicFromAsyncAction(
    {
        request: setRecordPermissionForGroupRequest,
        success: setPermissionForGroupSuccess
    },
    (payload, { userGroupClient }) =>
        userGroupClient.setRecordPermissionForGroup(payload.userGroupId, payload),
    (req, resp) => notifyUserGroupPermissionSet(resp)
);

const setPricelistRecordPermission$: RootEpic = (action$, state$, { userGroupClient }) =>
    action$.pipe(
        filter(setPricelistRecordPermissionsForGroupRequest.match),
        withLatestFrom(state$.pipe(map(state => state.pricelist.pricelists))),
        mergeMap(([action, pricelists]) =>
            from(
                userGroupClient.setRecordPermissionsForGroup(action.payload.userGroupId, {
                    actionType: action.payload.actionType,
                    hasPermission: action.payload.hasPermission,
                    resourceType: action.payload.resourceType,
                    userGroupId: action.payload.userGroupId,
                    recordIds: action.payload.applyToChildren
                        ? [
                              action.payload.recordId,
                              ...getAllPricelistChildren(action.payload.recordId, Object.values(pricelists))
                          ]
                        : [action.payload.recordId]
                })
            ).pipe(
                mergeMap(resp =>
                    of(setPermissionsForGroupSuccess(resp), notifyUserGroupPermissionsSet(resp))
                ),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const removeUserGroup$ = createEpicFromAsyncAction(
    removeUserGroup,
    (payload, { userGroupClient }) => userGroupClient.removeUserGroup(payload).then(() => payload),
    req => notifyUserGroupRemoved(req)
);

export default {
    fetchUserGroups$,
    createUserGroup$,
    updateUserGroup$,
    addMembersToGroup$,
    removeMemberFromGroup$,
    setGroupPagePermission$,
    removeUserGroup$,
    setGroupRecordPermission$,
    setPricelistRecordPermission$
};
